import { useCallback } from 'react';
import { useSubscribedPlan } from '../../../renew-plan/v1/hooks/use-subscribed-plan';
import { PlanAddonTypes } from '../../../renew-plan/v1/typings';

/**
 * Utility hook for checking calling addon availability.
 * Checks WA-CALL (legacy) first, then Calling-License (new addon).
 * Use this everywhere calling feature access needs to be gated — do NOT check
 * PlanAddonTypes.WaVoiceAddon or PlanAddonTypes.CallingLicense directly.
 */
export function useCallingAddon() {
  const { isAddonPresent, canShowFreeTrial, hasTrialEnded, getTrialAddon, addons, loading } = useSubscribedPlan(false);

  /** True if org has an active calling addon (WA-CALL or Calling-License). */
  const hasCallingAddon = isAddonPresent(PlanAddonTypes.WaVoiceAddon) || isAddonPresent(PlanAddonTypes.CallingLicense);

  /**
   * True when org is specifically on the new Calling-License addon (not legacy WA-CALL).
   * When true, per-agent calling license checks apply (hasCallingLicense on UserDetails).
   */
  const isCallingLicenseOrg = isAddonPresent(PlanAddonTypes.CallingLicense);

  /**
   * True if WA-CALL addon exists in the addons list regardless of expiry.
   * Used to suppress the free trial offer for orgs that already had a paid WA-CALL plan.
   */
  const hadWaCallAddon = addons.some((addon) => addon.addonId === PlanAddonTypes.WaVoiceAddon);

  /**
   * True if the org qualifies for the Calling-License free trial:
   * no active calling addon, no prior WA-CALL subscription (even expired), and never started a Calling-License trial.
   */
  const canShowCallingFreeTrial =
    !hasCallingAddon && !hadWaCallAddon && canShowFreeTrial(PlanAddonTypes.CallingLicense);

  /**
   * True if the Calling-License trial has ended and org has no active calling addon.
   * Used to decide whether to show the "Trial ended" modal.
   */
  const hasCallingTrialEnded =
    hasTrialEnded(PlanAddonTypes.CallingLicense) && !isAddonPresent(PlanAddonTypes.WaVoiceAddon);

  /** Returns the Calling-License trial addon entry (used for end-date display in TrialEndedModal). */
  const getCallingTrialAddon = useCallback(() => getTrialAddon(PlanAddonTypes.CallingLicense), [getTrialAddon]);

  return {
    hasCallingAddon,
    isCallingLicenseOrg,
    canShowCallingFreeTrial,
    hasCallingTrialEnded,
    getCallingTrialAddon,
    isSubscriptionLoading: loading,
  };
}
